import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ANONYMOUS, api, call } from "./client";
import { keys } from "./queries";
import type { ApprovalScopeIn, InviteIn, PlatformTenantIn, Role } from "./types";

/**
 * Signing in, invites, people and the platform console. Public calls (invite links, password
 * reset) need nobody signed in; accepting an invite with a new password goes anonymously so a
 * development sign-in can't stand in for the invited person.
 */

export const accessKeys = {
  options: ["auth-options"] as const,
  session: ["auth-session"] as const,
  people: ["people"] as const,
  tenants: ["platform-tenants"] as const,
  tenant: (slug: string) => ["platform-tenant", slug] as const,
  team: ["platform-team"] as const,
};

/* Public ---------------------------------------------------------------------------------- */

export const useSignInOptions = () =>
  useQuery({
    queryKey: accessKeys.options,
    queryFn: () => call(api.GET("/api/v1/auth/options", { headers: ANONYMOUS })),
    staleTime: Infinity,
  });

export const signInWithPassword = (email: string, password: string) =>
  call(api.POST("/api/v1/auth/password", { body: { email, password }, headers: ANONYMOUS }));

export const peekInvite = (token: string) =>
  call(api.POST("/api/v1/invites/peek", { body: { token }, headers: ANONYMOUS }));

/** Accept as whoever is signed in (Microsoft, a ZeaOps session, or development). */
export const acceptInviteSignedIn = (token: string) =>
  call(api.POST("/api/v1/invites/accept", { body: { token } }));

/** Accept as a new account with a password; nobody's sign-in goes with it. */
export const acceptInviteWithPassword = (token: string, name: string, password: string) =>
  call(
    api.POST("/api/v1/invites/accept", {
      body: { token, name, password },
      headers: ANONYMOUS,
    }),
  );

/** Accept in development as the invited person themselves. */
export const acceptInviteAsDev = (token: string, email: string) =>
  call(
    api.POST("/api/v1/invites/accept", {
      body: { token },
      headers: { ...ANONYMOUS, Authorization: `Bearer dev:${email}` },
    }),
  );

export const forgotPassword = (email: string) =>
  call(api.POST("/api/v1/auth/password/forgot", { body: { email }, headers: ANONYMOUS }));

export const peekReset = (token: string) =>
  call(api.POST("/api/v1/auth/password/reset/peek", { body: { token }, headers: ANONYMOUS }));

export const resetPassword = (token: string, password: string) =>
  call(api.POST("/api/v1/auth/password/reset", { body: { token, password }, headers: ANONYMOUS }));

/* Signed in -------------------------------------------------------------------------------- */

export const useSession = (enabled = true) =>
  useQuery({
    queryKey: accessKeys.session,
    queryFn: () => call(api.GET("/api/v1/auth/session")),
    enabled,
    retry: false,
    staleTime: 60_000,
  });

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { current_password: string | null; new_password: string }) =>
      call(api.POST("/api/v1/auth/password/change", { body })),
  });
}

/* People in this tenant (admins) ------------------------------------------------------------ */

export const usePeople = (enabled = true) =>
  useQuery({
    queryKey: accessKeys.people,
    queryFn: () => call(api.GET("/api/v1/people")),
    enabled,
  });

function usePeopleChanged() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: accessKeys.people });
    void qc.invalidateQueries({ queryKey: keys.users });
    void qc.invalidateQueries({ queryKey: keys.audit });
  };
}

export function useInvitePerson() {
  const changed = usePeopleChanged();
  return useMutation({
    mutationFn: (body: InviteIn) => call(api.POST("/api/v1/people/invites", { body })),
    onSuccess: changed,
  });
}

export function useResendInvite() {
  const changed = usePeopleChanged();
  return useMutation({
    mutationFn: (id: string) =>
      call(
        api.POST("/api/v1/people/invites/{invite_id}/resend", {
          params: { path: { invite_id: id } },
        }),
      ),
    onSuccess: changed,
  });
}

export function useWithdrawInvite() {
  const changed = usePeopleChanged();
  return useMutation({
    mutationFn: async (id: string) => {
      const { response, error } = await api.DELETE("/api/v1/people/invites/{invite_id}", {
        params: { path: { invite_id: id } },
      });
      if (!response.ok) await call(Promise.resolve({ error, response }));
    },
    onSuccess: changed,
  });
}

export function useChangePerson() {
  const changed = usePeopleChanged();
  return useMutation({
    mutationFn: ({
      id,
      role,
      team,
      approval_scope,
    }: {
      id: string;
      role?: Role;
      team?: string;
      approval_scope?: ApprovalScopeIn;
    }) =>
      call(
        api.PATCH("/api/v1/people/{user_id}", {
          params: { path: { user_id: id } },
          body: { role: role ?? null, team: team ?? null, approval_scope: approval_scope ?? null },
        }),
      ),
    onSuccess: changed,
  });
}

export function useRemovePerson() {
  const changed = usePeopleChanged();
  return useMutation({
    mutationFn: async (id: string) => {
      const { response, error } = await api.DELETE("/api/v1/people/{user_id}", {
        params: { path: { user_id: id } },
      });
      if (!response.ok) await call(Promise.resolve({ error, response }));
    },
    onSuccess: changed,
  });
}

export function useResetLink() {
  const changed = usePeopleChanged();
  return useMutation({
    mutationFn: (id: string) =>
      call(api.POST("/api/v1/people/{user_id}/reset-link", { params: { path: { user_id: id } } })),
    onSuccess: changed,
  });
}

/* The platform console (superadmins) ------------------------------------------------------- */

export const usePlatformTenants = () =>
  useQuery({
    queryKey: accessKeys.tenants,
    queryFn: () => call(api.GET("/api/v1/platform/tenants")),
  });

export const usePlatformTenant = (slug: string) =>
  useQuery({
    queryKey: accessKeys.tenant(slug),
    queryFn: () => call(api.GET("/api/v1/platform/tenants/{slug}", { params: { path: { slug } } })),
  });

function usePlatformChanged() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: accessKeys.tenants });
    void qc.invalidateQueries({ queryKey: ["platform-tenant"] });
    void qc.invalidateQueries({ queryKey: accessKeys.team });
  };
}

export function useCreateTenant() {
  const changed = usePlatformChanged();
  return useMutation({
    mutationFn: (body: PlatformTenantIn) => call(api.POST("/api/v1/platform/tenants", { body })),
    onSuccess: changed,
  });
}

export function useChangeTenant(slug: string) {
  const changed = usePlatformChanged();
  return useMutation({
    mutationFn: (body: { status?: "active" | "suspended" }) =>
      call(
        api.PATCH("/api/v1/platform/tenants/{slug}", {
          params: { path: { slug } },
          body: {
            status: body.status ?? null,
            name: null,
            environment_label: null,
            timezone: null,
          },
        }),
      ),
    onSuccess: changed,
  });
}

export function useInviteAdmin(slug: string) {
  const changed = usePlatformChanged();
  return useMutation({
    mutationFn: (body: { email: string; name: string | null }) =>
      call(
        api.POST("/api/v1/platform/tenants/{slug}/invites", { params: { path: { slug } }, body }),
      ),
    onSuccess: changed,
  });
}

export function useRotateSignalToken(slug: string) {
  return useMutation({
    mutationFn: () =>
      call(
        api.POST("/api/v1/platform/tenants/{slug}/signal-token", { params: { path: { slug } } }),
      ),
  });
}

export const useSuperadmins = () =>
  useQuery({
    queryKey: accessKeys.team,
    queryFn: () => call(api.GET("/api/v1/platform/superadmins")),
  });

export function useInviteSuperadmin() {
  const changed = usePlatformChanged();
  return useMutation({
    mutationFn: (body: { email: string; name: string | null }) =>
      call(api.POST("/api/v1/platform/superadmins/invites", { body })),
    onSuccess: changed,
  });
}

export function useRemoveSuperadmin() {
  const changed = usePlatformChanged();
  return useMutation({
    mutationFn: async (email: string) => {
      const { response, error } = await api.DELETE("/api/v1/platform/superadmins/{email}", {
        params: { path: { email } },
      });
      if (!response.ok) await call(Promise.resolve({ error, response }));
    },
    onSuccess: changed,
  });
}

export function useResendPlatformInvite() {
  const changed = usePlatformChanged();
  return useMutation({
    mutationFn: (id: string) =>
      call(
        api.POST("/api/v1/platform/invites/{invite_id}/resend", {
          params: { path: { invite_id: id } },
        }),
      ),
    onSuccess: changed,
  });
}

export function useWithdrawPlatformInvite() {
  const changed = usePlatformChanged();
  return useMutation({
    mutationFn: async (id: string) => {
      const { response, error } = await api.DELETE("/api/v1/platform/invites/{invite_id}", {
        params: { path: { invite_id: id } },
      });
      if (!response.ok) await call(Promise.resolve({ error, response }));
    },
    onSuccess: changed,
  });
}

/** The link someone opens to accept an invite. */
export const inviteUrl = (token: string) => `${window.location.origin}/invite/${token}`;
export const resetUrl = (token: string) => `${window.location.origin}/reset-password/${token}`;
